import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Search,
  CalendarDays,
  CreditCard,
  UserCircle2,
  LifeBuoy,
  Mail,
  Phone,
} from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import enLocale from "@/locales/en.json";

const FAQS_EN = (enLocale as { help: { items: { q: string; a: string; cat: string }[] } }).help.items;

export const Route = createFileRoute("/help")({
  head: () => ({
    meta: [
      { title: "Help center — Spayzo" },
      { name: "description", content: "Search articles and find answers about bookings, payments and your Spayzo account." },
      { property: "og:title", content: "Help center — Spayzo" },
      { property: "og:description", content: "Find quick answers and guides for using Spayzo." },
      { property: "og:url", content: "https://spayzo.lovable.app/help" },
    ],
    links: [{ rel: "canonical", href: "https://spayzo.lovable.app/help" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS_EN.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: HelpPage,
});

type Category = "all" | "booking" | "payments" | "account";

function HelpPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<Category>("all");

  const CATEGORIES: { id: Category; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: "all", label: t("help.categories.all"), icon: LifeBuoy },
    { id: "booking", label: t("help.categories.booking"), icon: CalendarDays },
    { id: "payments", label: t("help.categories.payments"), icon: CreditCard },
    { id: "account", label: t("help.categories.account"), icon: UserCircle2 },
  ];

  const FAQS = (t("help.items", { returnObjects: true }) as { q: string; a: string; cat: Exclude<Category, "all"> }[]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter((f) => {
      if (category !== "all" && f.cat !== category) return false;
      if (!q) return true;
      return f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
    });
  }, [query, category, FAQS]);

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <SiteHeader />

      <main className="flex-1">
        <section className="border-b bg-gradient-to-b from-muted/40 to-transparent">
          <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:py-20">
            <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
              {t("help.hero.title")}
            </h1>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
              {t("help.hero.subtitle")}
            </p>
            <div className="relative mx-auto mt-8 max-w-2xl">
              <Search className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("help.hero.search")}
                className="h-14 rounded-full ps-11 pe-4 text-base shadow-soft"
              />
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const active = category === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  className={cn(
                    "flex flex-col items-start gap-3 rounded-2xl border p-4 text-start transition-all",
                    active
                      ? "border-[#5958F3] bg-[#5958F3] text-white shadow-card"
                      : "bg-card hover:border-foreground/30 hover:shadow-soft",
                  )}
                >
                  <span
                    className={cn(
                      "inline-flex h-9 w-9 items-center justify-center rounded-xl",
                      active ? "bg-white/20" : "bg-muted",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-medium">{c.label}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-12">
            <h2 className="font-display text-2xl font-semibold tracking-tight">
              {t("help.faq.title")}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {filtered.length} {filtered.length === 1 ? t("help.faq.result") : t("help.faq.results")}
            </p>

            {filtered.length === 0 ? (
              <div className="mt-6 rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">
                {t("help.faq.empty")}
              </div>
            ) : (
              <Accordion type="single" collapsible className="mt-6 divide-y rounded-2xl border bg-card">
                {filtered.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-0 px-5">
                    <AccordionTrigger className="text-start text-base font-medium">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>

          <div className="mt-12 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border bg-card p-6 shadow-soft">
              <Phone className="h-5 w-5 text-foreground" />
              <h3 className="mt-3 font-display text-lg font-semibold">{t("help.contact.callTitle")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("help.contact.callDesc")}
              </p>
              <Button asChild className="mt-4 rounded-full">
                <a href="tel:+966500000000">{t("help.contact.callBtn")}</a>
              </Button>
            </div>
            <div className="rounded-2xl border bg-card p-6 shadow-soft">
              <Mail className="h-5 w-5 text-foreground" />
              <h3 className="mt-3 font-display text-lg font-semibold">{t("help.contact.emailTitle")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("help.contact.emailDesc")}
              </p>
              <Button asChild variant="outline" className="mt-4 rounded-full">
                <a href="mailto:support@spayzo.app">{t("help.contact.emailBtn")}</a>
              </Button>
            </div>
          </div>

          <p className="mt-10 text-center text-xs text-muted-foreground">
            {t("help.seeAlso")}{" "}
            <Link to="/cancellation" className="underline hover:text-foreground">{t("footer.cancellation")}</Link>,{" "}
            <Link to="/privacy" className="underline hover:text-foreground">{t("footer.privacy")}</Link>{" "}
            <Link to="/terms" className="underline hover:text-foreground">{t("footer.terms")}</Link>.
          </p>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
